import React, { useEffect, useState, useCallback } from 'react';
import { Card, Tabs, Form, InputNumber, Input, Switch, Button, Table, Tag, Space, Select, Statistic, Row, Col, message, Popconfirm, Typography } from 'antd';
import apiClient from '../../../shared/services/apiClient';

const { Text } = Typography;
const API = '/api/admin/affiliate';
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

/** 推广返利管理（规则见 api app/service/billing/AffiliateService.php） */
const Settings = () => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        apiClient.get(`${API}/settings`).then((r) => {
            const d = r.data?.data;
            if (d) form.setFieldsValue({ ...d, product_rates: JSON.stringify(d.product_rates || {}) });
        });
    }, [form]);
    const save = async (v) => {
        let pr;
        try {
            pr = JSON.parse(v.product_rates || '{}');
        } catch (_) {
            message.error('分产品比例必须是 JSON，例如 {"domain":3}');
            return;
        }
        setSaving(true);
        try {
            const r = await apiClient.put(`${API}/settings`, { ...v, product_rates: pr });
            if (r.data?.success) message.success('已保存');
            else message.error(r.data?.error || '保存失败');
        } finally {
            setSaving(false);
        }
    };
    return (
        <Form form={form} layout="vertical" onFinish={save} style={{ maxWidth: 560 }}>
            <Form.Item name="enabled" label="开启推广返利" valuePropName="checked"><Switch /></Form.Item>
            <Space wrap>
                <Form.Item name="rate" label="返佣比例 %"><InputNumber min={0} max={50} /></Form.Item>
                <Form.Item name="months" label="持续月数" tooltip="被邀请人注册后多少个月内的付费计佣"><InputNumber min={0} max={120} /></Form.Item>
                <Form.Item name="hold_days" label="冻结天数" tooltip="冻结期内退款/作废的账单佣金作废"><InputNumber min={0} max={180} /></Form.Item>
                <Form.Item name="cookie_days" label="邀请链接有效天数"><InputNumber min={1} max={365} /></Form.Item>
            </Space>
            <Form.Item name="product_rates" label="分产品比例（JSON）" tooltip='按账单行类型覆盖：domain（域名注册/续费/转入）、ssl、llm（LLM 划转）。例 {"domain":3,"llm":5}'>
                <Input />
            </Form.Item>
            <Form.Item name="welcome_coupon_code" label="新人券券码" tooltip="通过邀请链接注册的新用户自动发这张券（需先在「优惠码」里建好，可见性选券包）。留空 = 不发">
                <Input placeholder="例如 WELCOME5" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>保存</Button>
        </Form>
    );
};

const REF_STATUS = { active: ['green', '正常'], flagged: ['orange', '疑似自邀'], blocked: ['red', '已封禁'] };
const Referrals = () => {
    const [data, setData] = useState({ items: [], total: 0 });
    const [q, setQ] = useState({ page: 1, status: '', keyword: '' });
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiClient.get(`${API}/referrals`, { params: q });
            if (r.data?.success) setData(r.data.data);
        } finally {
            setLoading(false);
        }
    }, [q]);
    useEffect(() => { load(); }, [load]);
    const setStatus = async (id, status) => {
        const r = await apiClient.put(`${API}/referrals/${id}/status`, { status });
        if (r.data?.success) { message.success('已更新'); load(); } else message.error(r.data?.error || '失败');
    };
    return (
        <>
            <Space style={{ marginBottom: 12 }} wrap>
                <Select style={{ width: 140 }} value={q.status} onChange={(status) => setQ({ ...q, status, page: 1 })}
                    options={[{ value: '', label: '全部状态' }, ...Object.entries(REF_STATUS).map(([v, [, l]]) => ({ value: v, label: l }))]} />
                <Input.Search allowClear placeholder="邮箱 / 邀请码" onSearch={(keyword) => setQ({ ...q, keyword, page: 1 })} style={{ width: 240 }} />
            </Space>
            <Table rowKey="referee_id" loading={loading} dataSource={data.items} scroll={{ x: 900 }}
                pagination={{ current: q.page, total: data.total, pageSize: 20, onChange: (page) => setQ({ ...q, page }) }}
                columns={[
                    { title: '时间', dataIndex: 'created_at', width: 170 },
                    { title: '邀请人', dataIndex: 'referrer_email', render: (v, r) => <span>{v} <Text type="secondary">#{r.referrer_id}</Text></span> },
                    { title: '被邀请人', dataIndex: 'referee_email', render: (v, r) => <span>{v} <Text type="secondary">#{r.referee_id}</Text></span> },
                    { title: '来源', dataIndex: 'source', width: 90 },
                    { title: '注册 IP', dataIndex: 'signup_ip', width: 140 },
                    { title: '累计佣金', dataIndex: 'commission', align: 'right', render: money },
                    { title: '状态', key: 'status', render: (_, r) => <Space><Tag color={REF_STATUS[r.status]?.[0]}>{REF_STATUS[r.status]?.[1] || r.status}</Tag>{r.flag_reason && <Text type="secondary">{r.flag_reason}</Text>}</Space> },
                    {
                        title: '操作', key: 'act', width: 140, render: (_, r) => (
                            <Space>
                                {r.status !== 'active' && <Button size="small" onClick={() => setStatus(r.referee_id, 'active')}>放行</Button>}
                                {r.status !== 'blocked' && (
                                    <Popconfirm title="封禁后不再计佣，冻结中的佣金作废" onConfirm={() => setStatus(r.referee_id, 'blocked')}>
                                        <Button size="small" danger>封禁</Button>
                                    </Popconfirm>
                                )}
                            </Space>
                        ),
                    },
                ]} />
        </>
    );
};

const COM_STATUS = { pending: ['gold', '冻结中'], credited: ['green', '已入账'], reversed: ['default', '已作废'] };
const Commissions = () => {
    const [data, setData] = useState({ items: [], total: 0, summary: [] });
    const [q, setQ] = useState({ page: 1, status: '' });
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
        apiClient.get(`${API}/commissions`, { params: q }).then((r) => r.data?.success && setData(r.data.data)).finally(() => setLoading(false));
    }, [q]);
    const sum = (s) => data.summary.find((x) => x.status === s) || { n: 0, amount: 0 };
    return (
        <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
                {Object.entries(COM_STATUS).map(([s, [, l]]) => (
                    <Col key={s} xs={8}><Card size="small"><Statistic title={`${l}（${sum(s).n} 笔）`} value={money(sum(s).amount)} /></Card></Col>
                ))}
            </Row>
            <Select style={{ width: 140, marginBottom: 12 }} value={q.status} onChange={(status) => setQ({ status, page: 1 })}
                options={[{ value: '', label: '全部状态' }, ...Object.entries(COM_STATUS).map(([v, [, l]]) => ({ value: v, label: l }))]} />
            <Table rowKey="id" loading={loading} dataSource={data.items} scroll={{ x: 1000 }}
                pagination={{ current: q.page, total: data.total, pageSize: 20, onChange: (page) => setQ({ ...q, page }) }}
                columns={[
                    { title: '时间', dataIndex: 'created_at', width: 170 },
                    { title: '邀请人', dataIndex: 'referrer_email' },
                    { title: '被邀请人', dataIndex: 'referee_email' },
                    { title: '来源', key: 'src', render: (_, r) => `${r.source_type === 'invoice' ? '账单' : 'LLM 划转'} #${r.source_id}` },
                    { title: '计佣金额', dataIndex: 'base_amount', align: 'right', render: money },
                    { title: '比例', dataIndex: 'rate', align: 'right', render: (v) => `${Number(v)}%` },
                    { title: '佣金', dataIndex: 'amount', align: 'right', render: (v) => <Text strong>{money(v)}</Text> },
                    { title: '状态', key: 'st', render: (_, r) => <Tag color={COM_STATUS[r.status]?.[0]}>{COM_STATUS[r.status]?.[1]}</Tag> },
                    { title: '入账时间', key: 'at', render: (_, r) => r.credited_at || r.available_at },
                ]} />
        </>
    );
};

const AffiliateAdmin = () => (
    <Card title="推广返利">
        <Tabs items={[
            { key: 'referrals', label: '邀请关系', children: <Referrals /> },
            { key: 'commissions', label: '佣金', children: <Commissions /> },
            { key: 'settings', label: '设置', children: <Settings /> },
        ]} />
    </Card>
);

export default AffiliateAdmin;
